import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { customColumnKey, customValueIssues, type CustomObservationDefinition } from './customDefinitions';
import type { ClinicalSubject } from './types';
export interface CustomProposedEntry {
  event_type: `CUSTOM.${string}`;
  schema_version: number;
  subject_id: string;
  payload: { value: string | number | boolean };
}
export function CustomObservationEntry({
  definitions,
  subjects,
  onSave,
  disabled = false,
}: {
  definitions: CustomObservationDefinition[];
  subjects: ClinicalSubject[];
  onSave: (entries: CustomProposedEntry[], occurredAt: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [time, setTime] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const fields = definitions
    .filter((d) => d.enabled)
    .flatMap((d) =>
      subjects.filter((s) => s.subject_type === d.subject).map((s) => ({ d, s, key: `${customColumnKey(d)}/${s.id}` })),
    );
  if (!fields.length) return null;
  async function save() {
    const entries: CustomProposedEntry[] = [];
    const errors: string[] = [];
    for (const { d, s, key } of fields) {
      const raw = values[key];
      if (raw === undefined || raw === '') continue;
      const value = d.value_type === 'number' ? Number(raw) : d.value_type === 'boolean' ? raw === 'true' : raw;
      errors.push(...customValueIssues(d, value));
      entries.push({ event_type: d.event_type, schema_version: d.version, subject_id: s.id, payload: { value } });
    }
    if (!entries.length) errors.push('Enter at least one custom observation.');
    if (!time || !Number.isFinite(Date.parse(time))) errors.push('Enter a valid observation time.');
    if (errors.length) {
      setError(errors.join(' '));
      return;
    }
    setSaving(true);
    try {
      await onSave(entries, new Date(time).toISOString());
      setValues({});
      setError('');
    } catch {
      setError('Save did not complete. Values are retained.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}>
      <h3>Practice-specific observations</h3>
      <p>
        Custom fields use published practice definitions. Suggested values are applied only when explicitly selected.
      </p>
      <Field>
        <FieldLabel htmlFor="custom-observation-time">Custom observation time (this device’s local time)</FieldLabel>
        <Input
          id="custom-observation-time"
          type="datetime-local"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={disabled || saving}
        />
      </Field>
      <div className="grid gap-4 md:grid-cols-2">
        {fields.map(({ d, s, key }) => (
          <Field key={key}>
            <FieldLabel htmlFor={key}>
              {s.label} · {d.label}
              {d.unit ? ` (${d.unit})` : ''} · v{d.version}
            </FieldLabel>
            {d.value_type === 'boolean' || d.value_type === 'enum' ? (
              <Select
                value={
                  values[key] === undefined || values[key] === ''
                    ? '__unset'
                    : `choice:${(d.value_type === 'boolean' ? ['true', 'false'] : d.choices).indexOf(values[key])}`
                }
                onValueChange={(v) =>
                  setValues((old) => ({
                    ...old,
                    [key]:
                      v === '__unset'
                        ? ''
                        : (d.value_type === 'boolean' ? ['true', 'false'] : d.choices)[Number(v.slice(7))],
                  }))
                }
                disabled={disabled || saving}>
                <SelectTrigger id={key} aria-label={`${s.label} ${d.label}`} className="min-h-11">
                  <SelectValue placeholder="Not documented" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="__unset">Not documented</SelectItem>
                    {(d.value_type === 'boolean' ? ['true', 'false'] : d.choices).map((value, index) => (
                      <SelectItem key={value} value={`choice:${index}`}>
                        {d.value_type === 'boolean' ? (value === 'true' ? 'Yes' : 'No') : value}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={key}
                value={values[key] ?? ''}
                type={d.value_type === 'number' ? 'number' : 'text'}
                inputMode={d.value_type === 'number' ? 'decimal' : undefined}
                step="any"
                className="min-h-11"
                onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                disabled={disabled || saving}
              />
            )}{' '}
            {d.suggested_value !== undefined && d.suggested_value !== null && (
              <Button
                type="button"
                variant="outline"
                disabled={disabled || saving}
                onClick={() => setValues((v) => ({ ...v, [key]: String(d.suggested_value) }))}>
                Use practice suggestion: {String(d.suggested_value)}
              </Button>
            )}
          </Field>
        ))}
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" disabled={disabled || saving}>
        {saving ? 'Saving…' : 'Save custom observations'}
      </Button>
    </form>
  );
}
